import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, Camera, Trash2, CircleAlert, Lock, Mail, Copy, CheckCircle2, Link2 } from "lucide-react";
import { createEmployee, uploadEmployeePhoto, createInvitation, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
    name: "",
    email: "",
    phone: "",
    job_title: "",
    team: "",
    employment_start_date: "",
    active: true,
    hourly_wage_dkk: "",
    hourly_cost_internal_dkk: "",
    hourly_rate_customer_dkk: "",
    registration_number: "",
    can_receive_emergency_jobs: false,
    internal_notes: "",
    send_invitation: false,
    invitation_role: "medarbejder",
};

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ACCEPT_MIME = "image/jpeg,image/png,image/webp";

const INVITATION_ROLES = [
    { v: "medarbejder", l: "Medarbejder" },
    { v: "kontor", l: "Kontor" },
    { v: "admin", l: "Admin" },
];

function Section({ title, children }) {
    return (
        <div className="pb-4 border-b border-slate-100 last:border-b-0">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">{title}</h3>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Field({ label, hint, children, testid, htmlFor }) {
    return (
        <div>
            <Label htmlFor={htmlFor} className="text-slate-700 text-sm">{label}{hint && <span className="text-slate-400 text-xs ml-1">{hint}</span>}</Label>
            <div className="mt-1.5" data-testid={testid ? `${testid}-wrap` : undefined}>{children}</div>
        </div>
    );
}

export default function CreateEmployeeDialog({ open, onClose, onCreated }) {
    const { user } = useAuth();
    const isPrivileged = user && (user.role === "owner" || user.role === "admin");

    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [photoError, setPhotoError] = useState("");
    const [invite, setInvite] = useState(null); // {token, name, email, role, ...} after success
    const [copied, setCopied] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (open) {
            setForm(EMPTY);
            setPhoto(null);
            setPhotoPreview("");
            setPhotoError("");
            setInvite(null);
            setCopied(false);
        }
    }, [open]);

    const upd = (k) => (e) => {
        const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const pickPhoto = () => fileRef.current && fileRef.current.click();

    const onPhotoChange = (e) => {
        setPhotoError("");
        const f = e.target.files && e.target.files[0];
        if (!f) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
            setPhotoError("Kun JPEG, PNG eller WebP tilladt.");
            return;
        }
        if (f.size > MAX_PHOTO_BYTES) {
            setPhotoError("Filen er over 5 MB.");
            return;
        }
        setPhoto(f);
        const reader = new FileReader();
        reader.onload = (ev) => setPhotoPreview(ev.target.result || "");
        reader.readAsDataURL(f);
    };

    const clearPhoto = () => {
        setPhoto(null);
        setPhotoPreview("");
        setPhotoError("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const buildPayload = () => {
        const p = { name: form.name.trim() };
        if (form.email.trim()) p.email = form.email.trim();
        if (form.phone.trim()) p.phone = form.phone.trim();
        if (form.job_title.trim()) p.job_title = form.job_title.trim();
        if (form.team.trim()) p.team = form.team.trim();
        if (form.employment_start_date) p.employment_start_date = form.employment_start_date;
        p.active = !!form.active;
        if (isPrivileged) {
            if (form.hourly_wage_dkk !== "") p.hourly_wage_dkk = Number(form.hourly_wage_dkk);
            if (form.hourly_cost_internal_dkk !== "") p.hourly_cost_internal_dkk = Number(form.hourly_cost_internal_dkk);
        }
        if (form.hourly_rate_customer_dkk !== "") p.hourly_rate_customer_dkk = Number(form.hourly_rate_customer_dkk);
        if (form.registration_number.trim()) p.registration_number = form.registration_number.trim();
        p.can_receive_emergency_jobs = !!form.can_receive_emergency_jobs;
        if (form.internal_notes.trim()) p.internal_notes = form.internal_notes.trim();
        return p;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Navn er obligatorisk.");
            return;
        }
        if (form.send_invitation && !form.email.trim()) {
            toast.error("Email er obligatorisk når du sender login-invitation.");
            return;
        }
        setSaving(true);
        try {
            // 1) Create employee profile
            const created = await createEmployee(buildPayload());
            // 2) Upload photo if selected
            if (photo && created?.id) {
                try {
                    await uploadEmployeePhoto(created.id, photo);
                } catch (photoErr) {
                    toast.error("Medarbejder oprettet, men billed-upload fejlede: " + formatApiError(photoErr));
                }
            }
            // 3) Send invitation if requested
            if (form.send_invitation && form.email.trim()) {
                try {
                    const inv = await createInvitation({
                        name: form.name.trim(),
                        email: form.email.trim(),
                        role: form.invitation_role,
                        team: form.team.trim(),
                    });
                    setInvite(inv);
                    toast.success("Medarbejder oprettet og invitation klar — kopiér linket");
                } catch (invErr) {
                    toast.error("Profil oprettet, men invitationen fejlede: " + formatApiError(invErr));
                }
            } else {
                toast.success("Medarbejder oprettet");
            }
            if (onCreated) onCreated(created);
            // If no invitation to display, close immediately
            if (!form.send_invitation) {
                onClose();
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const inviteLink = invite ? `${window.location.origin}/accept-invite/${invite.token}` : "";
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(inviteLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Kunne ikke kopiere");
        }
    };

    // Post-invitation success view
    if (invite) {
        return (
            <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            Medarbejder oprettet
                        </DialogTitle>
                    </DialogHeader>
                    <div className="mt-3 space-y-3" data-testid="create-emp-invite-success">
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-emerald-800 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
                            <span>Profil oprettet for <strong>{invite.name}</strong>. Send linket nedenfor til <strong>{invite.email}</strong> — når medarbejderen klikker og opretter adgangskode, er kontoen klar.</span>
                        </div>
                        <div>
                            <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5 text-slate-500" />Invitations-link</Label>
                            <div className="flex items-center gap-2 mt-1.5">
                                <Input data-testid="create-emp-invite-link" readOnly value={inviteLink} className="h-11 font-mono text-xs flex-1" onFocus={(ev) => ev.target.select()} />
                                <Button type="button" data-testid="create-emp-invite-copy" onClick={copyLink} className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white">
                                    {copied ? <><CheckCircle2 className="w-4 h-4 mr-1.5" />Kopieret</> : <><Copy className="w-4 h-4 mr-1.5" />Kopiér link</>}
                                </Button>
                            </div>
                            <p className="text-xs text-slate-500 mt-2">Linket udløber om 14 dage. Del det via mail, SMS eller besked.</p>
                        </div>
                        <div className="flex justify-end pt-1">
                            <Button type="button" onClick={onClose} data-testid="create-emp-invite-done" className="bg-slate-900 hover:bg-slate-800 text-white">Færdig</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-600" />
                        Opret medarbejder
                    </DialogTitle>
                    <p className="text-xs text-slate-500 mt-1">Opretter medarbejder-profilen. Slå login-invitation til hvis medarbejderen også skal have en konto med rolle og adgang.</p>
                </DialogHeader>

                <form onSubmit={submit} className="mt-3 space-y-5" data-testid="create-emp-form">
                    {/* --- Grundoplysninger --- */}
                    <Section title="Grundoplysninger">
                        <div className="flex items-center gap-4">
                            <div className="w-20 h-20 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center relative shrink-0" data-testid="create-emp-photo-preview">
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                                ) : (
                                    <User className="w-8 h-8 text-slate-300" />
                                )}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" size="sm" onClick={pickPhoto} data-testid="create-emp-photo-pick" className="h-8">
                                        <Camera className="w-3.5 h-3.5 mr-1.5" />
                                        {photo ? "Skift billede" : "Upload billede"}
                                    </Button>
                                    {photo && (
                                        <Button type="button" variant="outline" size="sm" onClick={clearPhoto} data-testid="create-emp-photo-clear" className="h-8 text-red-600 hover:bg-red-50">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                </div>
                                <p className="text-[11px] text-slate-500">JPEG, PNG eller WebP · max 5 MB</p>
                                {photoError && <p className="text-[11px] text-red-600 flex items-center gap-1"><CircleAlert className="w-3 h-3" />{photoError}</p>}
                                <input ref={fileRef} type="file" accept={ACCEPT_MIME} onChange={onPhotoChange} className="hidden" data-testid="create-emp-photo-input" />
                            </div>
                        </div>

                        <Field label="Navn" testid="create-emp-name" htmlFor="emp-name">
                            <Input id="emp-name" data-testid="create-emp-name" required value={form.name} onChange={upd("name")} placeholder="Fx Mads Sørensen" className="h-10" />
                        </Field>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Email" hint={form.send_invitation ? "(obligatorisk til invitation)" : "(valgfri)"} testid="create-emp-email" htmlFor="emp-email">
                                <Input id="emp-email" data-testid="create-emp-email" type="email" required={form.send_invitation} value={form.email} onChange={upd("email")} placeholder="mads@firma.dk" className="h-10" />
                            </Field>
                            <Field label="Telefon" hint="(valgfri)" testid="create-emp-phone" htmlFor="emp-phone">
                                <Input id="emp-phone" data-testid="create-emp-phone" value={form.phone} onChange={upd("phone")} placeholder="+45 12 34 56 78" className="h-10" />
                            </Field>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Stilling" hint="(fx elektriker eller lærling)" testid="create-emp-job-title" htmlFor="emp-job">
                                <Input id="emp-job" data-testid="create-emp-job-title" value={form.job_title} onChange={upd("job_title")} placeholder="Elektriker" className="h-10" />
                            </Field>
                            <Field label="Team / afdeling" hint="(valgfri)" testid="create-emp-team" htmlFor="emp-team">
                                <Input id="emp-team" data-testid="create-emp-team" value={form.team} onChange={upd("team")} placeholder="Service Nord" className="h-10" />
                            </Field>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Startdato" hint="(valgfri)" testid="create-emp-start-date" htmlFor="emp-date">
                                <Input id="emp-date" data-testid="create-emp-start-date" type="date" value={form.employment_start_date} onChange={upd("employment_start_date")} className="h-10" />
                            </Field>
                            <Field label="Status">
                                <label className="flex items-center gap-2 h-10">
                                    <input type="checkbox" checked={form.active} onChange={upd("active")} data-testid="create-emp-active" className="w-4 h-4" />
                                    <span className="text-sm text-slate-700">{form.active ? "Aktiv" : "Inaktiv"}</span>
                                </label>
                            </Field>
                        </div>
                    </Section>

                    {/* --- Priser --- */}
                    <Section title="Priser">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Field label="Timeløn (kr)" hint={isPrivileged ? "" : "(kun owner/admin)"} testid="create-emp-wage" htmlFor="emp-wage">
                                <div className="relative">
                                    <Input id="emp-wage" data-testid="create-emp-wage" type="number" min="0" step="0.01" value={form.hourly_wage_dkk} onChange={upd("hourly_wage_dkk")} disabled={!isPrivileged} placeholder="0" className="h-10" />
                                    {!isPrivileged && <Lock className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />}
                                </div>
                            </Field>
                            <Field label="Intern timeomkostning (kr)" hint={isPrivileged ? "" : "(kun owner/admin)"} testid="create-emp-cost" htmlFor="emp-cost">
                                <div className="relative">
                                    <Input id="emp-cost" data-testid="create-emp-cost" type="number" min="0" step="0.01" value={form.hourly_cost_internal_dkk} onChange={upd("hourly_cost_internal_dkk")} disabled={!isPrivileged} placeholder="0" className="h-10" />
                                    {!isPrivileged && <Lock className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />}
                                </div>
                            </Field>
                            <Field label="Kundetimepris (kr)" testid="create-emp-customer-rate" htmlFor="emp-cust">
                                <Input id="emp-cust" data-testid="create-emp-customer-rate" type="number" min="0" step="0.01" value={form.hourly_rate_customer_dkk} onChange={upd("hourly_rate_customer_dkk")} placeholder="650" className="h-10" />
                            </Field>
                        </div>
                        <p className="text-[11px] text-slate-500">Alle beløb i DKK. Timeløn og intern timeomkostning er kun synlige for owner/admin.</p>
                    </Section>

                    {/* --- Drift --- */}
                    <Section title="Drift">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Nummerplade" hint="(valgfri)" testid="create-emp-plate" htmlFor="emp-plate">
                                <Input id="emp-plate" data-testid="create-emp-plate" value={form.registration_number} onChange={upd("registration_number")} placeholder="AB 12 345" className="h-10 uppercase" maxLength={20} />
                            </Field>
                            <Field label="Telegram">
                                <div className="h-10 flex items-center gap-2 text-sm text-slate-600" data-testid="create-emp-telegram-status">
                                    <span className="inline-block w-2 h-2 rounded-full bg-slate-300" />
                                    Ikke forbundet — sættes via Telegram-flow
                                </div>
                            </Field>
                        </div>
                        <label className="flex items-center gap-2 pt-1">
                            <input type="checkbox" checked={form.can_receive_emergency_jobs} onChange={upd("can_receive_emergency_jobs")} data-testid="create-emp-emergency" className="w-4 h-4" />
                            <span className="text-sm text-slate-700">Kan modtage akutte jobs</span>
                        </label>
                        <Field label="Interne noter" hint="(valgfri)" testid="create-emp-notes" htmlFor="emp-notes">
                            <textarea id="emp-notes" data-testid="create-emp-notes" value={form.internal_notes} onChange={upd("internal_notes")} rows={3} maxLength={5000} className="w-full rounded-md border border-slate-200 p-2 text-sm" placeholder="Certifikater, kompetencer eller andet…" />
                        </Field>
                    </Section>

                    {/* --- Login-adgang (invitation) --- */}
                    <Section title="Login-adgang (valgfri)">
                        <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-lg border border-slate-200 hover:border-blue-200 hover:bg-blue-50/30 transition-colors">
                            <input
                                type="checkbox"
                                checked={form.send_invitation}
                                onChange={upd("send_invitation")}
                                data-testid="create-emp-send-invitation"
                                className="w-4 h-4 mt-0.5"
                            />
                            <div className="flex-1">
                                <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-blue-600" /> Send login-invitation efter oprettelse</div>
                                <div className="text-xs text-slate-500 mt-0.5">Medarbejderen får et link til at oprette adgangskode og logge ind.</div>
                            </div>
                        </label>
                        {form.send_invitation && (
                            <div className="pl-6 pt-1" data-testid="create-emp-invite-role-panel">
                                <Label className="text-slate-700 text-sm">Systemrolle</Label>
                                <div className="flex gap-1.5 mt-1.5">
                                    {INVITATION_ROLES.map((r) => (
                                        <button
                                            key={r.v}
                                            type="button"
                                            data-testid={`create-emp-invite-role-${r.v}`}
                                            onClick={() => setForm((f) => ({ ...f, invitation_role: r.v }))}
                                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${form.invitation_role === r.v ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                                        >{r.l}</button>
                                    ))}
                                </div>
                                <p className="text-[11px] text-slate-500 mt-2">Invitationen sendes til <strong>{form.email || "(indtast email ovenfor)"}</strong>. Linket udløber om 14 dage.</p>
                            </div>
                        )}
                    </Section>

                    <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white -mx-6 px-6 pb-1">
                        <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="create-emp-cancel">Annullér</Button>
                        <Button type="submit" disabled={saving} data-testid="create-emp-submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            {form.send_invitation ? "Opret & send invitation" : "Opret medarbejder"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
